import * as React from "react";
import {
  autoUpdate,
  flip,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useMergeRefs,
} from "@floating-ui/react";
import { useIcon } from "@salt-ds/core";

import { Button } from "../controls/Button";
import { usePortalScopeClassName } from "../floating/usePortalScope";
import { mergeProps } from "../utils/mergeProps";
import { useFormFieldProps } from "../salt/form-field-context";
import { PillInput, type PillInputProps } from "./PillInput";
import { useControlled } from "./useControlled";
import { useForkRef } from "./useForkRef";
import {
  ListControlContext,
  useListControl,
  type ListControlProps,
  type OptionValue,
} from "./useListControl";

/**
 * ComboBox — a text field that filters a list, replacing `@salt-ds/core`'s behind
 * `searchable-select.tsx`.
 *
 * ── IT IS A Dropdown WHOSE TRIGGER YOU CAN TYPE INTO, AND THAT CHANGES THE ARIA ───────────
 *
 * Dropdown's trigger is a button; this one is an input. So focus stays in the input at all
 * times and the active option is named by `aria-activedescendant` rather than being focused —
 * moving real focus into the list would take it out of the field the user is typing in, and
 * the next keystroke would go nowhere.
 *
 * Everything downstream follows from that: options carry `tabIndex={-1}`, and focus or clicks
 * landing anywhere in the list are bounced straight back to the input.
 *
 * ── TYPING OPENS THE LIST; FOCUS ALONE MUST NOT ───────────────────────────────────────────
 *
 * A Tab through a form would otherwise open every combo box it passed, and the one left open
 * would swallow the next keystroke. Focus is still TRACKED (`focusedState`) because the pills
 * show their close buttons only while the field has focus — but tracking focus and opening on
 * focus are different things, and the vendor's open handler is careful to separate them.
 */
/**
 * Extends the WHOLE of `PillInputProps` (minus `onPillRemove`, which ComboBox owns) rather than
 * naming the props it happens to need. That is the vendor's shape and it matters: a ComboBox is
 * a PillInput the caller can also configure — `placeholder`, `startAdornment`,
 * `validationStatus`, `textAlign` and the rest all belong to the field, and re-listing a subset
 * silently drops whichever ones the list forgot. `placeholder` is exactly how this was caught.
 */
export interface ComboBoxProps<Item = string>
  extends Omit<PillInputProps, "onPillRemove" | "pills" | "onChange">,
    Omit<ListControlProps<Item>, "valueToString"> {
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Escape hatch onto the floating list. MERGED, never spread — see the note at the call. */
  OverlayProps?: React.HTMLAttributes<HTMLDivElement>;
  /** Commit the active option on Tab. Defaults to on for single-select, off for multi. */
  selectOnTab?: boolean;
  valueToString?: (item: Item) => string;
}

const defaultValueToString = (item: string) => String(item);

export const ComboBox = React.forwardRef<HTMLDivElement, ComboBoxProps>(function ComboBox(
  {
    bordered = false,
    children,
    className,
    defaultOpen,
    defaultSelected,
    defaultValue,
    disabled: disabledProp,
    endAdornment: endAdornmentProp,
    inputProps: inputPropsProp,
    inputRef: inputRefProp,
    multiselect,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    onOpenChange,
    onSelectionChange,
    open,
    OverlayProps,
    readOnly: readOnlyProp,
    selectOnTab = !multiselect,
    selected,
    truncate,
    value,
    valueToString = defaultValueToString,
    variant = "primary",
    ...rest
  },
  ref,
) {
  const { CollapseIcon, ExpandIcon } = useIcon();
  const { a11yProps, disabled: formFieldDisabled, readOnly: formFieldReadOnly } =
    useFormFieldProps();
  const disabled = Boolean(disabledProp) || formFieldDisabled;
  const readOnly = Boolean(readOnlyProp) || formFieldReadOnly;

  const inputRef = React.useRef<HTMLInputElement>(null);
  const handleInputRef = useForkRef(inputRef, inputRefProp);
  /*
   * Set when the user TYPES, cleared on any other key. It is what makes typing "ac" highlight
   * the first match ready for Enter, without a bare ArrowDown doing the same thing — an
   * auto-highlight that appears when you did not type is an option you might commit by accident.
   */
  const shouldAutoSelectRef = React.useRef(false);

  const listControl = useListControl<string>({
    defaultOpen,
    defaultSelected,
    disabled,
    multiselect,
    onOpenChange,
    onSelectionChange,
    open,
    readOnly,
    selected,
    valueToString,
  });
  const {
    activeState,
    clear,
    focusedState,
    getFirstOption,
    getLastOption,
    getOptionAfter,
    getOptionBefore,
    getOptionPageAbove,
    getOptionPageBelow,
    getOptionsMatching,
    listRef,
    openKey,
    openState,
    selectedState,
    setActive,
    setFocusVisibleState,
    setFocusedState,
    setListRef,
    setOpen,
    setSelectedState,
  } = listControl;

  const [valueState, setValueState] = useControlled<string>({
    controlled: value,
    default:
      defaultValue ??
      (selectedState.length === 1 && !multiselect ? valueToString(selectedState[0]) : ""),
  });

  /**
   * ComboBox REPLACES the list control's `select` rather than calling it, because selecting
   * here also has to write the input's text — the field shows the chosen label, not a caret
   * where the user left off. Multi-select clears the text instead: the selection is now shown
   * as a pill, and leaving the typed fragment behind would filter the list to one entry.
   */
  const select = (event: React.SyntheticEvent, option: OptionValue) => {
    if (option.disabled || disabled || readOnly) return;

    let newSelected = [option.value];
    if (multiselect) {
      newSelected = selectedState.includes(option.value)
        ? selectedState.filter((item) => item !== option.value)
        : selectedState.concat([option.value]);
    }
    setSelectedState(newSelected);
    const labels = getOptionsMatching((candidate) => newSelected.includes(candidate.value)).map(
      (candidate) => valueToString(candidate.data.value),
    );
    setValueState(multiselect ? "" : labels[0]);
    onSelectionChange?.(event, newSelected);
    if (!multiselect) setOpen(false);
  };

  const removePill = (event: React.SyntheticEvent, itemToRemove: string) => {
    if (!multiselect || disabled || readOnly) return;
    const newSelected = selectedState.includes(itemToRemove)
      ? selectedState.filter((item) => item !== itemToRemove)
      : selectedState.concat([itemToRemove]);
    setSelectedState(newSelected);
    setValueState("");
    onSelectionChange?.(event, newSelected);
  };

  const hasValidChildren = React.Children.toArray(children).filter(Boolean).length > 0;
  /*
   * The portal scope is derived from the REFERENCE element, not from the component: the list
   * renders in a portal, outside this subtree, so it would otherwise inherit the document's
   * density and breakpoint rather than the field's. That is the vendor defect this hook exists
   * to work around.
   */
  const [referenceEl, setReferenceEl] = React.useState<Element | null>(null);
  const portalScope = usePortalScopeClassName(referenceEl);
  const listOpen = (openState || focusedState) && !readOnly && hasValidChildren;

  const { context, floatingStyles, refs } = useFloating({
    open: openState && !readOnly && hasValidChildren,
    onOpenChange: (nextOpen, _event, reason) => {
      const focusNotBlur = reason === "focus" && nextOpen;
      if (reason === "focus") setFocusedState(nextOpen);
      if (reason === "focus" && !nextOpen) setFocusVisibleState(false);
      if (readOnly || focusNotBlur) return;
      setOpen(nextOpen);
      if (nextOpen) inputRef.current?.focus();
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(1),
      size({
        apply({
          rects,
          elements: sized,
          availableHeight,
        }: {
          rects: { reference: { width: number } };
          elements: { floating: HTMLElement };
          availableHeight: number;
        }) {
          sized.floating.style.setProperty("--overlay-minWidth", `${rects.reference.width}px`);
          sized.floating.style.setProperty(
            "--overlay-maxHeight",
            `max(calc((var(--salt-size-base) + var(--salt-spacing-100)) * 5), calc(${availableHeight}px - var(--salt-spacing-100)))`,
          );
        },
      }),
      flip({ fallbackStrategy: "initialPlacement" }),
    ],
  });

  const { getFloatingProps, getReferenceProps } = useInteractions([
    useDismiss(context),
    useFocus(context),
    // `keyboardHandlers: false` because this component owns its keys — floating-ui's would open
    // the list on Enter, which here means "commit the active option".
    // `toggle: false` because a click lands in a text field the user is already typing in;
    // toggling would close the list under them.
    useClick(context, { keyboardHandlers: false, toggle: false }),
  ]);

  const mergedRef = useMergeRefs([
    refs.setReference,
    ref,
    (node: HTMLDivElement | null) => setReferenceEl(node),
  ]);
  const mergedListRef = useMergeRefs([setListRef, refs.setFloating]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event as unknown as React.KeyboardEvent<HTMLDivElement>);
    // Auto-select survives key REPEAT so holding a letter still highlights; any fresh key ends it.
    if (!event.repeat) shouldAutoSelectRef.current = false;
    if (readOnly) return;

    if (!openState && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      // Remembered so the list opens at the END for ArrowUp — opening at the top and then
      // moving the wrong way is the classic version of this bug.
      setOpen(true, undefined, event.key);
      return;
    }

    let newActive: { data: OptionValue<string> } | undefined;
    switch (event.key) {
      case "ArrowDown":
        newActive = activeState ? getOptionAfter(activeState) : getFirstOption();
        break;
      case "ArrowUp":
        newActive = activeState ? getOptionBefore(activeState) : getLastOption();
        break;
      case "Home":
        newActive = getFirstOption();
        break;
      case "End":
        newActive = getLastOption();
        break;
      case "PageUp": {
        // With nothing highlighted, a page-up starts from the END of the list — the fallback has
        // to exist first, because an empty list has no page to move within.
        const pageUpFrom = activeState ?? getLastOption()?.data;
        newActive = pageUpFrom ? getOptionPageAbove(pageUpFrom) : undefined;
        break;
      }
      case "PageDown": {
        const pageDownFrom = activeState ?? getFirstOption()?.data;
        newActive = pageDownFrom ? getOptionPageBelow(pageDownFrom) : undefined;
        break;
      }
      case "Enter":
        // Swallowed for a disabled option so Enter does not submit the surrounding form while
        // the user believes they are choosing something.
        if (openState && activeState?.disabled) {
          event.preventDefault();
          return;
        }
        if (!openState || !activeState) return;
        select(event, activeState);
        // Multi-select does NOT prevent default: the list stays open and Enter may still mean
        // "submit" to the form around it.
        if (!multiselect) event.preventDefault();
        break;
      case "Tab":
        // Tab commits the highlighted option on the way out, so a user who typed and tabbed
        // keeps what they were looking at instead of losing it.
        if (
          openState &&
          selectOnTab &&
          activeState &&
          !activeState.disabled &&
          !selectedState.includes(activeState.value)
        ) {
          select(event, activeState);
        }
        break;
    }

    if (newActive) setFocusVisibleState(true);
    if (newActive && newActive.data.id !== activeState?.id) {
      event.preventDefault();
      setActive(newActive.data);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    if (!openState) setOpen(true, "input");
    // Emptying the field clears the selection in single-select: the text IS the selection there,
    // so leaving a stale value behind would show a choice the field no longer displays.
    if (nextValue === "" && !multiselect) clear(event);
    setValueState(nextValue);
    if (nextValue === "") setActive(undefined);
    else shouldAutoSelectRef.current = true;
    onChange?.(event);
  };

  React.useLayoutEffect(() => {
    if (value) shouldAutoSelectRef.current = true;
  }, [value]);

  React.useEffect(() => {
    if (!openState || !value) return;
    // A microtask, because the filtered children have to commit before "the first option" means
    // anything — read synchronously it is the first option of the PREVIOUS query.
    queueMicrotask(() => {
      const first = getFirstOption();
      if (first && shouldAutoSelectRef.current) setActive(first.data);
    });
  }, [value, setActive, openState, getFirstOption]);

  React.useEffect(() => {
    if (!openState) {
      setActive(undefined);
      return;
    }
    let next = selectedState.length
      ? getOptionsMatching((option) => option.value === selectedState[0]).pop()
      : undefined;
    if (!next) {
      if (openKey.current === "ArrowDown") {
        next = getFirstOption();
        setFocusVisibleState(true);
      } else if (openKey.current === "ArrowUp") {
        next = getLastOption();
        setFocusVisibleState(true);
      }
    }
    if (next) setActive(next.data);
    // Deliberately keyed on `openState` alone: this is "what should be highlighted when the list
    // OPENS", and re-running it as the user types would drag the highlight back to the selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openState]);

  const buttonId = React.useId();
  const listId = React.useId();

  const showOptionsButton = (
    <Button
      appearance="transparent"
      aria-controls={openState ? listId : undefined}
      aria-expanded={openState}
      aria-haspopup="listbox"
      aria-label="Show options"
      aria-labelledby={[buttonId, a11yProps?.["aria-labelledby"]].filter(Boolean).join(" ")}
      disabled={disabled}
      onClick={(event) => {
        if (readOnly) return;
        // Stopped so the field's own click handler does not immediately reopen what this closed.
        event.stopPropagation();
        setFocusVisibleState(false);
        setOpen(!openState, "manual");
      }}
      // Focus is bounced to the input: the button is a pointer affordance, and letting it hold
      // focus would break `aria-activedescendant`, which requires focus to stay on the combobox.
      onFocus={() => inputRef.current?.focus()}
      tabIndex={-1}
    >
      {openState ? <CollapseIcon aria-hidden /> : <ExpandIcon aria-hidden />}
    </Button>
  );

  const endAdornment =
    !readOnly && hasValidChildren ? (
      <>
        {endAdornmentProp}
        {showOptionsButton}
      </>
    ) : (
      endAdornmentProp
    );

  return (
    <ListControlContext.Provider value={{ ...listControl, select } as never}>
      <PillInput
        aria-activedescendant={activeState?.id}
        bordered={bordered}
        /*
         * `saltComboBox` only. The vendor also emits `saltComboBox-focused` and
         * `saltComboBox-focusVisible`, and NOTHING defines either — not `@salt-ds/core`'s
         * stylesheet, not the injected component CSS, not this app. They are classes that
         * resolve to no rule, which fails silently: the element declares a style and never
         * receives one.
         *
         * `-focused` is redundant anyway; the PillInput inside already carries
         * `saltPillInput-focused`, which IS styled. `-focusVisible` has no styled equivalent
         * anywhere. Emitting them here would trip this repo's dead-class ratchet, and rightly.
         *
         * The BASE class stays: it is the key for Salt's `useClassNameInjection` extension
         * point, so an application can register styling against it.
         */
        className={["saltComboBox", className].filter(Boolean).join(" ")}
        disabled={disabled}
        // Empty rather than an em dash when a read-only field HAS selections: the pills already
        // show them, and the marker exists for the genuinely empty case.
        emptyReadOnlyMarker={readOnly && selectedState.length > 0 ? "" : undefined}
        endAdornment={endAdornment}
        hidePillClose={!focusedState || readOnly}
        inputProps={{
          "aria-controls": openState && !readOnly ? listId : undefined,
          "aria-expanded": !readOnly ? openState : undefined,
          "aria-readonly": readOnly ? "true" : undefined,
          onKeyDown: handleKeyDown,
          ...inputPropsProp,
        }}
        inputRef={handleInputRef}
        onChange={handleChange}
        onPillRemove={(event, index) => {
          event.stopPropagation();
          removePill(event, String(selectedState[index]));
        }}
        pills={multiselect ? selectedState.map((item) => valueToString(item)) : []}
        readOnly={readOnly}
        ref={mergedRef}
        // A combobox that cannot be typed into is a textbox, and calling it a combobox would
        // promise a list the user cannot open.
        role={readOnly ? "textbox" : "combobox"}
        // The wrapper is -1 so a click lands on it without stealing focus from the input inside.
        tabIndex={!disabled ? -1 : undefined}
        truncate={truncate && !focusedState && !openState}
        // The field's text is OWNED here, not by the input: selecting an option has to write the
        // chosen label into it, and multi-select has to clear it. An input left to control its
        // own value types fine and then silently ignores every selection.
        value={valueState}
        variant={variant}
        {...getReferenceProps({
          onBlur: (event) => {
            event.persist();
            // Blur INTO the list is not a blur: clicking an option must not report the field as
            // abandoned, so the handler only fires when focus really left the whole widget.
            if (!listRef.current || !listRef.current.contains(event.relatedTarget as Node)) {
              onBlur?.(event as unknown as React.FocusEvent<HTMLDivElement>);
              shouldAutoSelectRef.current = false;
            }
          },
          onFocus: (event) => {
            if (event.currentTarget === inputRef.current) setFocusedState(true);
            else setActive(undefined);
            onFocus?.(event as unknown as React.FocusEvent<HTMLDivElement>);
          },
          ...rest,
        })}
      />
      {listOpen ? (
        <div className={portalScope}>
          <div
            aria-multiselectable={multiselect}
            id={listId}
            role="listbox"
            /*
             * MERGED, not spread. `OverlayProps` is the caller's escape hatch, and a plain
             * spread lets its `className` REPLACE `saltOptionList` — the list would keep the
             * consumer's class and lose every rule that makes it a list.
             */
            {...mergeProps(
              {
                className: openState
                  ? "saltOptionList"
                  : "saltOptionList saltOptionList-collapsed",
              },
              getFloatingProps({
                onClick: () => inputRef.current?.focus(),
                onFocus: () => inputRef.current?.focus(),
                onMouseLeave: () => setActive(undefined),
                onMouseOver: () => setFocusVisibleState(false),
                ...OverlayProps,
              }) as Record<string, unknown>,
            )}
            ref={mergedListRef}
            style={floatingStyles}
            tabIndex={-1}
          >
            <div className="saltOptionListBase">{children}</div>
          </div>
        </div>
      ) : null}
    </ListControlContext.Provider>
  );
}) as <Item = string>(
  props: ComboBoxProps<Item> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;
